import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useApi } from '../lib/api.js';
import { useLang } from '../lib/i18n.js';
import { PageHead, Badge } from '../components/ui.js';
import { Icon } from '../components/Icon.js';
import { formatMoney, type Currency } from '@chatrunner/schemas';

interface Product {
  product_id: string;
  title: string;
  description: string;
  base_price_cents: number;
  original_price_cents: number | null;
  currency: string;
  primary_image_url: string | null;
  embedding_state: string;
  source: 'platform' | 'direct';
}

export function Products() {
  const api = useApi();
  const { t } = useLang();
  const { data } = useQuery({
    queryKey: ['products'],
    queryFn: () => api.get<{ items: Product[] }>('/products'),
    refetchInterval: 10_000,
  });
  const items = data?.items ?? [];

  return (
    <div className="wrap section-gap">
      <PageHead
        title={t('page.products.title')}
        sub={t('page.products.sub')}
        actions={
          <>
            <Link to="/products/import" className="btn btn--ghost"><Icon name="upload" />{t('page.products.import')}</Link>
            <Link to="/products/new" className="btn btn--primary"><Icon name="plus" />{t('page.products.new')}</Link>
          </>
        }
      />

      <div className="between wrapf">
        <Badge kind="muted" plain>
          <span className="mono">{items.length}{items.length >= 500 ? '+' : ''}</span>&nbsp;{t('page.products.count')}
        </Badge>
        {items.length >= 500 && <span className="muted" style={{ fontSize: 13 }}>· {t('page.products.cap')}</span>}
      </div>

      {items.length === 0 ? (
        <div className="card card--pad" style={{ textAlign: 'center', padding: 48 }}>
          <div className="empty__icon" style={{ margin: '0 auto 14px' }}><Icon name="box" /></div>
          <div style={{ fontWeight: 700, fontSize: 16 }}>{t('page.products.empty.h')}</div>
          <div className="muted" style={{ fontSize: 13.5, marginTop: 6 }}>{t('page.products.empty.p')}</div>
          <div className="mt16"><Link to="/products/new" className="btn btn--primary"><Icon name="plus" />{t('page.products.new')}</Link></div>
        </div>
      ) : (
        <div className="pgrid">
          {items.map((p) => {
            const currency = p.currency as Currency;
            return (
              <div className="product" key={p.product_id}>
                <div className="product__img">
                  {p.primary_image_url ? <img src={p.primary_image_url} alt={p.title} /> : <Icon name="image" />}
                  <div className="product__emb">
                    {p.embedding_state === 'embedded'
                      ? <Badge kind="ok" plain icon="checkcircle">{t('embed.embedded')}</Badge>
                      : p.embedding_state === 'failed'
                      ? <Badge kind="danger" plain>{t('embed.failed')}</Badge>
                      : <Badge kind="warn" plain icon="refresh">{t('embed.pending')}</Badge>}
                  </div>
                </div>
                <div className="product__body">
                  <div className="product__title">{p.title}</div>
                  <div className="product__meta">
                    <span className="product__price">{formatMoney(p.base_price_cents, currency)}</span>
                    {p.original_price_cents != null && (
                      <span className="muted" style={{ fontSize: 12, textDecoration: 'line-through' }}>
                        {formatMoney(p.original_price_cents, currency)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
